import { useComputed, useSignal } from "@preact/signals";
import type { LoaderArgs, PageArgs } from "bunfx";
import { navigateTo, RedirectError } from "bunfx";
import { useEffect } from "preact/hooks";
import {
  IcoArchive,
  IcoChevronLeft,
  IcoSpinner,
  IcoStar,
  IcoTrash,
} from "../../../components/icons";
import { BrandedLayout, ConfirmDialog, TextArea } from "../../../components/ui";
import { usePageTitle } from "../../../hooks";
import { rpc } from "../../../rpc";
import { showError } from "../../../stores/errors";
import type { MimicState } from "../../../stores/mimic";
import {
  type BankQuestion,
  type BrandColor,
  isAdmin,
  type QuestionType,
  type User,
} from "../../../types";
import { getErrorMessage } from "../../../utils/errors";

type BankQuestionEditState = {
  currentUser: User;
  mimic: MimicState;
  orgId: string;
  orgName: string;
  brandColor: BrandColor;
  hasReports: boolean;
  workspaceId: string;
  question: BankQuestion;
  categories: string[];
};

export async function load(args: LoaderArgs): Promise<BankQuestionEditState> {
  const orgId = args.params.orgId ?? "";
  const questionId = args.params.questionId ?? "";

  const [authResult, orgsResult, orgResult] = await Promise.all([
    rpc.auth.me({}),
    rpc.orgs.list({}),
    rpc.orgs.get({ id: orgId }),
  ]);

  if (!authResult.user) {
    throw new RedirectError("/login");
  }

  const isOrgAdmin = isAdmin(orgResult.currentMember.role);
  if (!isOrgAdmin) {
    throw new RedirectError(`/orgs/${orgId}`);
  }

  const workspaceId = orgResult.organization.id;

  // Get brand color from list endpoint
  const orgFromList = orgsResult.organizations.find((o) => o.id === orgId);
  const brandColor = (orgFromList?.brandColor ?? "indigo") as BrandColor;

  const [questionResult, categoriesResult] = await Promise.all([
    rpc.questionBank.get({ questionId }),
    rpc.questionBank.getCategories({ workspaceId }),
  ]);

  return {
    currentUser: authResult.user as User,
    mimic: authResult.mimic ?? null,
    orgId,
    orgName: orgResult.organization.name,
    brandColor,
    hasReports: orgFromList?.hasReports ?? false,
    workspaceId,
    question: questionResult.question,
    categories: categoriesResult.categories,
  };
}

export function Page({ state }: PageArgs<typeof load>) {
  const {
    currentUser,
    mimic,
    orgId,
    orgName,
    brandColor,
    hasReports,
    question,
    categories,
  } = state.value;

  usePageTitle("Edit Question");

  // Form state
  const title = useSignal(question.title);
  const description = useSignal(question.description);
  const type = useSignal<QuestionType>(question.type);
  const ratingMax = useSignal(question.ratingMax);
  const category = useSignal(question.category ?? "");
  const isArchived = useSignal(question.archivedAt !== null);

  // UI state
  const saveStatus = useSignal<"idle" | "saving" | "saved">("idle");
  const showDeleteDialog = useSignal(false);
  const isDeleting = useSignal(false);
  const showNewCategoryDialog = useSignal(false);
  const pendingCategory = useSignal<string | null>(null);

  // Check if a category is new (not in existing list)
  const isNewCategory = (cat: string) => {
    if (!cat.trim()) return false;
    return !categories.some(
      (c) => c.toLowerCase() === cat.trim().toLowerCase(),
    );
  };

  // Handle category change with new category confirmation
  const handleCategoryChange = (newValue: string) => {
    // If clearing or selecting existing category, just set it
    if (!newValue.trim() || !isNewCategory(newValue)) {
      category.value = newValue;
      return;
    }
    // New category - ask for confirmation
    pendingCategory.value = newValue.trim();
    showNewCategoryDialog.value = true;
  };

  const confirmNewCategory = () => {
    if (pendingCategory.value) {
      category.value = pendingCategory.value;
    }
    pendingCategory.value = null;
    showNewCategoryDialog.value = false;
  };

  const cancelNewCategory = () => {
    pendingCategory.value = null;
    showNewCategoryDialog.value = false;
  };

  // Track if form is dirty
  const isDirty = useComputed(() => {
    return (
      title.value !== question.title ||
      description.value !== question.description ||
      type.value !== question.type ||
      ratingMax.value !== question.ratingMax ||
      (category.value || null) !== question.category
    );
  });

  // Auto-save on changes with debounce
  // Skip auto-save when new category dialog is open (waiting for user confirmation)
  useEffect(() => {
    if (
      !isDirty.value ||
      saveStatus.value === "saving" ||
      showNewCategoryDialog.value
    )
      return;

    const timeout = setTimeout(async () => {
      await handleSave();
    }, 1000);

    return () => clearTimeout(timeout);
  }, [
    title.value,
    description.value,
    type.value,
    ratingMax.value,
    category.value,
    showNewCategoryDialog.value,
  ]);

  async function handleSave() {
    if (saveStatus.value === "saving") return;

    saveStatus.value = "saving";
    try {
      await rpc.questionBank.update({
        questionId: question.id,
        title: title.value,
        description: description.value,
        type: type.value,
        ratingMax: ratingMax.value,
        category: category.value || null,
      });
      saveStatus.value = "saved";
      setTimeout(() => {
        if (saveStatus.value === "saved") {
          saveStatus.value = "idle";
        }
      }, 2000);
    } catch (err) {
      showError(getErrorMessage(err));
      saveStatus.value = "idle";
    }
  }

  async function handleArchiveToggle() {
    try {
      await rpc.questionBank.archive({
        questionId: question.id,
        archived: !isArchived.value,
      });
      isArchived.value = !isArchived.value;
    } catch (err) {
      showError(getErrorMessage(err));
    }
  }

  async function handleDelete() {
    if (isDeleting.value) return;

    isDeleting.value = true;
    try {
      await rpc.questionBank.remove({ questionId: question.id });
      navigateTo(`/orgs/${orgId}/forms/bank`);
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      isDeleting.value = false;
    }
  }

  // Keyboard shortcut for save
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        handleSave();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <BrandedLayout
      user={currentUser}
      mimic={mimic}
      orgId={orgId}
      orgName={orgName}
      brandColor={brandColor}
      activeTab="forms"
      isAdmin={true}
      hasReports={hasReports}
    >
      <div class="max-w-2xl mx-auto">
        {/* Header */}
        <div class="mb-6">
          <a
            href={`/orgs/${orgId}/forms/bank`}
            class="inline-flex items-center gap-1 text-sm text-text-muted hover:text-text transition-colors mb-4"
          >
            <IcoChevronLeft class="size-4" />
            Back to Question Bank
          </a>

          <div class="flex items-center justify-between">
            <h1 class="text-2xl font-bold text-text">Edit Question</h1>
            <SaveStatusIndicator status={saveStatus.value} />
          </div>
        </div>

        {/* Archived banner */}
        {isArchived.value && (
          <div class="mb-6 p-4 rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800">
            <div class="flex items-center gap-2 text-amber-800 dark:text-amber-200">
              <IcoArchive class="size-5" />
              <span class="font-medium">This question is archived</span>
            </div>
            <p class="text-sm text-amber-700 dark:text-amber-300 mt-1">
              Archived questions won't appear when adding questions to forms.
            </p>
          </div>
        )}

        {/* Form */}
        <div class="bg-surface border border-border rounded-xl p-6 space-y-6">
          {/* Title */}
          <div>
            <label
              htmlFor="title"
              class="block text-sm font-medium text-text mb-2"
            >
              Question Title
            </label>
            <TextArea
              id="title"
              value={title.value}
              onInput={(e) => {
                title.value = (e.target as HTMLTextAreaElement).value;
              }}
              placeholder="Enter the question..."
              class="w-full px-3 py-2 text-base rounded-lg border border-border bg-surface-hover focus:outline-none focus:ring-2 focus:ring-text-subtle text-text placeholder:text-text-subtle resize-none"
            />
          </div>

          {/* Description */}
          <div>
            <label
              htmlFor="description"
              class="block text-sm font-medium text-text mb-2"
            >
              Description{" "}
              <span class="text-text-muted font-normal">(optional)</span>
            </label>
            <TextArea
              id="description"
              value={description.value}
              onInput={(e) => {
                description.value = (e.target as HTMLTextAreaElement).value;
              }}
              placeholder="Add context or instructions for this question..."
              class="w-full px-3 py-2 text-sm rounded-lg border border-border bg-surface-hover focus:outline-none focus:ring-2 focus:ring-text-subtle text-text placeholder:text-text-subtle resize-none"
            />
          </div>

          {/* Question Type */}
          <div>
            <label class="block text-sm font-medium text-text mb-2">
              Question Type
            </label>
            <div class="flex gap-3">
              <TypeOption
                label="Short Answer"
                icon={<span class="text-lg font-medium">Aa</span>}
                selected={type.value === "short_answer"}
                onClick={() => {
                  type.value = "short_answer";
                }}
              />
              <TypeOption
                label="Rating"
                icon={<IcoStar class="size-5" />}
                selected={type.value === "rating"}
                onClick={() => {
                  type.value = "rating";
                }}
              />
            </div>
          </div>

          {/* Rating Max (only for rating type) */}
          {type.value === "rating" && (
            <div>
              <label
                htmlFor="ratingMax"
                class="block text-sm font-medium text-text mb-2"
              >
                Rating Scale
              </label>
              <select
                id="ratingMax"
                value={ratingMax.value}
                onChange={(e) => {
                  ratingMax.value = Number(
                    (e.target as HTMLSelectElement).value,
                  );
                }}
                class="px-3 py-2 rounded-lg border border-border bg-surface-hover focus:outline-none focus:ring-2 focus:ring-text-subtle text-text"
              >
                {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                  <option key={n} value={n}>
                    1 to {n}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Category */}
          <div>
            <label
              htmlFor="category"
              class="block text-sm font-medium text-text mb-2"
            >
              Category{" "}
              <span class="text-text-muted font-normal">(optional)</span>
            </label>
            <input
              type="text"
              id="category"
              list="category-suggestions"
              value={category.value}
              onInput={(e) => {
                // Allow typing freely, check on blur
                category.value = (e.target as HTMLInputElement).value;
              }}
              onBlur={(e) => {
                const val = (e.target as HTMLInputElement).value;
                if (val && isNewCategory(val)) {
                  handleCategoryChange(val);
                }
              }}
              placeholder="e.g., Engagement, Well-being, Goals"
              class="w-full px-3 py-2 rounded-lg border border-border bg-surface-hover focus:outline-none focus:ring-2 focus:ring-text-subtle text-text placeholder:text-text-subtle"
            />
            <datalist id="category-suggestions">
              {categories.map((cat) => (
                <option key={cat} value={cat} />
              ))}
            </datalist>
            <p class="text-xs text-text-muted mt-1">
              Categories help organize your questions
            </p>
          </div>
        </div>

        {/* Actions */}
        <div class="mt-6 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <button
              type="button"
              onClick={handleArchiveToggle}
              class="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
            >
              <IcoArchive class="size-4" />
              {isArchived.value ? "Restore" : "Archive"}
            </button>
            <button
              type="button"
              onClick={() => {
                showDeleteDialog.value = true;
              }}
              class="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
            >
              <IcoTrash class="size-4" />
              Delete
            </button>
          </div>

          <div class="text-sm text-text-muted">
            Used {question.usageCount}{" "}
            {question.usageCount === 1 ? "time" : "times"}
          </div>
        </div>
      </div>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={showDeleteDialog.value}
        title="Delete Question"
        confirmLabel={isDeleting.value ? "Deleting..." : "Delete"}
        onConfirm={handleDelete}
        onCancel={() => {
          showDeleteDialog.value = false;
        }}
      >
        Are you sure you want to delete this question? This action cannot be
        undone.
      </ConfirmDialog>

      {/* New category confirmation */}
      <ConfirmDialog
        open={showNewCategoryDialog.value}
        title="Create New Category"
        confirmLabel="Create Category"
        onConfirm={confirmNewCategory}
        onCancel={cancelNewCategory}
      >
        <p>
          "<strong>{pendingCategory.value}</strong>" is a new category that
          doesn't exist yet.
        </p>
        <p class="mt-2 text-text-muted">
          Are you sure you want to create this new category? Consider using an
          existing category if one fits.
        </p>
        {categories.length > 0 && (
          <div class="mt-3 p-3 bg-surface-hover rounded-lg">
            <p class="text-xs text-text-muted mb-2">Existing categories:</p>
            <div class="flex flex-wrap gap-1">
              {categories.map((cat) => (
                <span
                  key={cat}
                  class="px-2 py-0.5 bg-surface rounded text-xs text-text-muted"
                >
                  {cat}
                </span>
              ))}
            </div>
          </div>
        )}
      </ConfirmDialog>
    </BrandedLayout>
  );
}

// ============================================================================
// Components
// ============================================================================

function SaveStatusIndicator({
  status,
}: {
  status: "idle" | "saving" | "saved";
}) {
  if (status === "idle") return null;

  return (
    <div class="flex items-center gap-2 text-sm text-text-muted">
      {status === "saving" && (
        <>
          <IcoSpinner class="size-4 animate-spin" />
          <span>Saving...</span>
        </>
      )}
      {status === "saved" && (
        <span class="text-green-600 dark:text-green-400">Saved</span>
      )}
    </div>
  );
}

function TypeOption({
  label,
  icon,
  selected,
  onClick,
}: {
  label: string;
  icon: preact.JSX.Element;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-colors ${
        selected
          ? "border-text bg-surface-hover"
          : "border-border bg-surface hover:bg-surface-hover"
      }`}
    >
      <div
        class={`size-10 rounded-full flex items-center justify-center ${
          selected ? "bg-text text-bg" : "bg-surface-muted text-text-muted"
        }`}
      >
        {icon}
      </div>
      <span class={`font-medium ${selected ? "text-text" : "text-text-muted"}`}>
        {label}
      </span>
    </button>
  );
}
